/**
 * voice-v8 — „Verfassung"-Seite (#386-Ausbau, 2026-07-21, Martins Wunsch:
 * WHOOP als eigenes Sidebar-Element).
 *
 * Zeigt die koerperliche Verfassung aus WHOOP — Heute-Block (Erholung,
 * HRV, Ruhepuls, Schlaf mit Phasen, Belastung), 14-Tage-Verlauf und die
 * letzten Trainings. Der Mehrwert gegenueber der WHOOP-App ist die
 * VERKNUEPFUNG: der Frage-Knopf traegt die Werte direkt ins Gespraech.
 * Gerueste nach NORM v3 (page-sheet, buildPageHeader, 860px-Spalte).
 */
import {
  juengsteFrischeMood,
  sichtbareTrioZeilen,
  trioHatInnereQuelle,
  wienTag,
} from "./verfassung-mood.js";
import { buildPageHeader , buildLoadingHint } from "./sheet-ui.js";
import { whoopRing } from "./tool-cards.js";
import { setPageHash, clearPageHash } from "./page-routes.js";

interface Tag {
  datum: string | null;
  erholung_pct: number | null;
  hrv_ms: number | null;
  ruhepuls: number | null;
  spo2_pct?: number | null;
  hauttemp_c?: number | null;
  kalorien_kcal?: number | null;
  kalibriert_noch?: boolean;
  schlaf: {
    dauer_h: number | null;
    bedarf_h?: number | null;
    defizit_h?: number | null;
    atemfrequenz?: number | null;
    effizienz_pct: number | null;
    phasen?: {
      tief_min: number | null;
      rem_min: number | null;
      leicht_min: number | null;
      wach_min: number | null;
    } | null;
  } | null;
  tagesbelastung: number | null;
}

interface Workout {
  sport: string | null;
  start: string | null;
  dauer_min: number | null;
  belastung: number | null;
  puls: number | null;
  kcal: number | null;
}

interface Summary {
  ok: boolean;
  connected?: boolean;
  configured?: boolean;
  tage?: Tag[];
  workouts?: Workout[];
  error?: string;
}

/**
 * 2026-07-27 (Design-Audit): Deutsche Schreibweise fuer Zahlen, die als
 * Dezimalwert hereinkommen koennen. Ueberall sonst steht sauber ein Komma
 * (toFixed(1).replace(".", ",")) — nur die Belastung gab die rohe Zahl aus
 * und stand als „1.3" zwischen „6,9 h" und „95,9 %".
 * Ganze Zahlen bleiben ohne Nachkommastelle: „12" statt „12,0".
 */
function zahlDe(n: number): string {
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(".", ",");
}

function ampel(score: number | null): string {
  if (score == null) return "unbekannt";
  if (score >= 67) return "gut";
  if (score >= 34) return "mittel";
  return "niedrig";
}

/**
 * 2026-07-27 (Audit-Punkt 10): Am Telefon gibt es keine Maus — ein
 * title-Attribut ist dort unsichtbar. Antippen klappt EINE Zeile unter
 * `anker` auf; nochmal tippen schliesst sie. Es gibt immer nur eine offene
 * Zeile auf der Seite, deshalb genuegt EIN Element fuer alles.
 */
let tippZeile: HTMLParagraphElement | null = null;
let tippQuelle: HTMLElement | null = null;
function antippbar(el: HTMLElement, text: string, anker: HTMLElement = el): void {
  el.classList.add("vf-antippbar");
  el.setAttribute("role", "button");
  el.setAttribute("tabindex", "0");
  el.setAttribute("aria-expanded", "false");
  // Der Eigen-Text kommt mit ins Label — eine Trend-Saeule waere sonst
  // namenlos, und die Vorlese-Hilfe saehe nur „Schaltflaeche".
  const eigen = (el.textContent ?? "").trim().replace(/\s+/g, " ");
  el.setAttribute("aria-label", eigen ? `${eigen} – ${text}` : text);
  const zeigen = (): void => {
    // Nach einem Neu-Rendern zeigt tippQuelle auf ein Element, das nicht mehr
    // im Dokument haengt — sonst bliebe die Zeile fuer immer „schon offen".
    if (tippQuelle && !tippQuelle.isConnected) tippQuelle = null;
    if (!tippZeile) {
      tippZeile = document.createElement("p");
      tippZeile.className = "vf-tipp";
      tippZeile.setAttribute("role", "status");
    }
    tippQuelle?.setAttribute("aria-expanded", "false");
    const wieder = tippQuelle === el;
    tippZeile.remove();
    tippQuelle = null;
    if (wieder) return; // zweites Tippen schliesst
    anker.after(tippZeile);
    tippZeile.textContent = text;
    el.setAttribute("aria-expanded", "true");
    tippQuelle = el;
  };
  el.addEventListener("click", zeigen);
  el.addEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      zeigen();
    }
  });
}

const SPORT_DE: Record<string, string> = {
  activity: "Aktivität",
  running: "Laufen",
  cycling: "Radfahren",
  skiing: "Skifahren",
  snowboarding: "Snowboarden",
  walking: "Gehen",
  hiking: "Wandern",
  swimming: "Schwimmen",
  strength_training: "Krafttraining",
  weightlifting: "Krafttraining",
  functional_fitness: "Functional Fitness",
  yoga: "Yoga",
  pilates: "Pilates",
  tennis: "Tennis",
  soccer: "Fußball",
  surfing: "Surfen",
  climbing: "Klettern",
  martial_arts: "Kampfsport",
  golf: "Golf",
  spinning: "Spinning",
  rowing: "Rudern",
  meditation: "Meditation",
};

function sportName(s: string | null): string {
  if (!s) return "Training";
  const key = s.toLowerCase().replace(/[ -]/g, "_");
  return SPORT_DE[key] ?? s.charAt(0).toUpperCase() + s.slice(1);
}

function fmtDatum(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit" });
}

export function closeVerfassungView(): void {
  document.getElementById("verfassung-sheet")?.remove();
  clearPageHash("verfassung");
}

export async function openVerfassungView(): Promise<void> {
  if (document.getElementById("verfassung-sheet")) return;
  window.dispatchEvent(new CustomEvent("voice-v8:page-open"));

  const el = document.createElement("div");
  el.className = "sheet verfassung-sheet page-sheet";
  el.id = "verfassung-sheet";
  el.setAttribute("role", "dialog");

  const card = document.createElement("div");
  card.className = "sheet-card";
  const body = document.createElement("div");
  body.className = "vf-body page-body";
  card.appendChild(body);
  el.appendChild(card);
  card.prepend(buildPageHeader("Verfassung", closeVerfassungView));
  document.body.appendChild(el);
  /* 2026-08-30: Der eigene Aufruf ist entfallen — buildPageHeader meldet
     jede Seite mit geteiltem Kopf beim einen Wisch-Waechter an. Zwei
     Handler haetten denselben Rueckweg zweimal ausgeloest. */
  setPageHash("verfassung");

  // 2026-07-25 (Martin: "den Button 'Was heisst das heute?' oben griffbereit,
  // damit ich die Info schneller bekomme"): Der Knopf stand ganz unten, hinter
  // Verlauf, Trainings und Mustern — man musste an allem vorbeiscrollen, um
  // genau das zu fragen, was einen zuerst interessiert. Jetzt sitzt er direkt
  // unter dem Kopf und ist da, bevor die Daten geladen sind.
  const askRow = document.createElement("div");
  askRow.className = "vf-ask-row vf-ask-row--oben";
  const ask = document.createElement("button");
  ask.type = "button";
  ask.className = "btn btn--primary";
  ask.textContent = "Was heißt das für heute?";
  ask.addEventListener("click", () => {
    closeVerfassungView();
    window.dispatchEvent(
      new CustomEvent("voice-v8:ask-vespera", {
        detail: {
          text: "Schau auf meine aktuellen WHOOP-Werte und sag mir kurz und konkret, was sie für meinen heutigen Tag bedeuten.",
        },
      }),
    );
  });
  askRow.appendChild(ask);
  body.appendChild(askRow);

  // 2026-08-30 (Paket 6): dieselbe Ladeanzeige wie ueberall statt nacktem Text.
  const laden = buildLoadingHint("Lade Vitaldaten …");
  body.appendChild(laden);

  interface Kosmos {
    ok?: boolean;
    mond?: {
      name: string;
      symbol: string;
      beleuchtung_pct: number;
      naechsterVollmond: string;
    };
    kp?: { kp: number; label: string } | null;
    sonne?: { klasse: string; spitze: string; label: string } | null;
  }
  interface MoodDay {
    day: string;
    geist: number | null;
    seele: number | null;
    einzeiler: string | null;
    quelle: string;
  }
  interface Muster {
    ok?: boolean;
    moods?: MoodDay[];
    kosmos?: Array<{ day: string; kp: number | null; mondPct: number | null }>;
  }
  let s: Summary | null = null;
  let kosmos: Kosmos | null = null;
  let muster: Muster | null = null;
  try {
    const [r, k, m] = await Promise.all([
      fetch("/api/whoop/summary?days=14", { credentials: "include" }),
      fetch("/api/kosmos", { credentials: "include" }),
      fetch("/api/verfassung/muster?days=30", { credentials: "include" }),
    ]);
    s = (await r.json()) as Summary;
    kosmos = (await k.json().catch(() => null)) as Kosmos | null;
    muster = (await m.json().catch(() => null)) as Muster | null;
  } catch {
    s = null;
  }
  laden.remove();

  // ── Verfassung: nur Zeilen mit echter Quelle ──────────────────────────
  // Körper = WHOOP tage[0].erholung_pct.
  // Seele/Geist = juengsteFrischeMood aus /api/verfassung/muster (mood-job
  //   aus der Stimme). Fehlt die Zahl: Zeile weg. Kein „–“, kein Essay.
  const koerperScore = s?.ok && s.tage?.[0] ? s.tage[0].erholung_pct : null;
  const heuteKey = wienTag();
  const juengsteMood = juengsteFrischeMood(muster?.moods ?? [], heuteKey);
  const trioZeilen = sichtbareTrioZeilen({
    koerper: koerperScore,
    geist: juengsteMood?.geist ?? null,
    seele: juengsteMood?.seele ?? null,
  });
  if (trioHatInnereQuelle(trioZeilen)) {
    const trio = document.createElement("section");
    trio.className = "vf-trio";
    for (const z of trioZeilen) {
      /*
       * 2026-08-31 (Martin, mit Bild der Seite: "was soll das für eine
       * ausdruckslose Seite sein? kannst Du die bitte deutlich im Design
       * verbessern?").
       *
       * Vorher: drei Zeilen reiner Text, Name und Zahl, sonst nichts — keine
       * Farbe, kein Bezug zwischen den dreien, keine Skala. Genau DAS macht
       * eine Zahl ausdruckslos: 62 allein sagt nichts, 62 NEBEN 75 und 60
       * schon eher, und eine Farbe je Bereich macht den Unterschied auf
       * einen Blick sichtbar, ohne dass man die Zahlen erst vergleichen muss.
       *
       * ⚠️ Es gab bereits `.vf-trio-track`/`.vf-trio-fill` im CSS — toter
       * Code aus einem frueheren Anlauf: die Klassen wurden nirgends mehr
       * erzeugt (0 Treffer in dieser Datei), eine spaetere Regel hatte die
       * Spur sogar per `display: none` bewusst abgeschaltet. Jetzt entsteht
       * sie wirklich, mit eigener Farbe je Bereich statt dem alten flachen
       * Grau.
       *
       * Die drei Farben sind KEINE neuen Werte — `--ember` (Koerper, warm),
       * `--sage` (Geist, klar/ruhig) und `--plum` (Seele, tief) stehen schon
       * im Farbsystem und tragen an anderen Stellen der App dieselbe
       * Bedeutung.
       */
      const w = document.createElement("div");
      w.className = "vf-trio-zeile";
      w.dataset.bereich =
        z.label === "Körper" ? "koerper" : z.label === "Geist" ? "geist" : "seele";
      const kopf = document.createElement("div");
      kopf.className = "vf-trio-kopf";
      const l = document.createElement("span");
      l.className = "vf-trio-label";
      l.textContent = z.label;
      const v = document.createElement("span");
      v.className = "vf-trio-wert";
      v.textContent = `${z.wert}`;
      kopf.append(l, v);
      const spur = document.createElement("div");
      spur.className = "vf-trio-track";
      const fuellung = document.createElement("span");
      fuellung.className = "vf-trio-fill";
      fuellung.style.setProperty(
        "--anteil",
        String(Math.max(0, Math.min(100, z.wert))),
      );
      spur.appendChild(fuellung);
      w.append(kopf, spur);
      if (z.label === "Körper") {
        antippbar(w, "Erholung aus WHOOP (letzte Nacht)");
      } else if (juengsteMood?.einzeiler) {
        antippbar(w, juengsteMood.einzeiler);
      }
      trio.appendChild(w);
    }
    if (juengsteMood?.einzeiler) {
      const zeile = document.createElement("p");
      zeile.className = "vf-trio-einzeiler";
      zeile.textContent = `„${juengsteMood.einzeiler}"`;
      trio.appendChild(zeile);
    }
    /*
     * 2026-08-31 (Martin: "Ich möchte wissen, woraus sich Geist und Seele
     * zusammensetzt, was die Berechnungsparameter dahinter sind").
     *
     * Körper ist unmittelbar einleuchtend — die WHOOP-Erholung, ein
     * Messwert. Geist und Seele sind etwas anderes: eine taegliche
     * Einschaetzung von Claude Fable 5, gewonnen NICHT aus WHOOP, sondern
     * aus Martins eigenen Worten des Tages (Alltag-Verdichtungen, Antworten
     * auf Tiefe Fragen — server/mood-job.ts). Ohne diesen Satz sieht das
     * wie eine weitere Kennzahl aus, misst aber etwas grundsaetzlich
     * anderes — genau das war seine Frage.
     *
     * Bewusst eine leise Bildunterschrift, kein Tipp-Dialog: es beantwortet
     * die Frage sofort, ohne dass man erst herausfinden muss, dass es eine
     * Antwort zu tippen gibt.
     */
    if (trioZeilen.some((z) => z.label === "Geist" || z.label === "Seele")) {
      const herkunft = document.createElement("p");
      herkunft.className = "vf-trio-herkunft";
      herkunft.textContent =
        "Geist & Seele: von Claude aus deinen eigenen Worten des Tages geschätzt, jede Nacht neu. Körper: direkt aus dem WHOOP-Ring.";
      trio.appendChild(herkunft);
    }
    body.appendChild(trio);
  }

  // Kosmos-Zeile — unabhaengig von der WHOOP-Verbindung sichtbar.
  if (kosmos?.mond) {
    const kz = document.createElement("div");
    kz.className = "vf-kosmos";
    const mondTeil = document.createElement("span");
    mondTeil.className = "vf-kp";
    const voll = new Date(kosmos.mond.naechsterVollmond);
    const vollStr = isNaN(voll.getTime())
      ? kosmos.mond.naechsterVollmond
      : voll.toLocaleDateString("de-AT", { day: "2-digit", month: "2-digit" });
    // Ticket 779 (2026-07-27): Der Mond trug drei Angaben in EINER Pille —
    // Symbol, Phase, Prozent und Vollmond-Datum. Beim laengsten Fall
    // ("Zunehmender Mond") sind das rund 43 Zeichen, also etwa 312 px; auf
    // einem iPhone bleiben nach den Seitenraendern rund 360 px. Es passte
    // gerade eben, und `white-space: nowrap` haette es bei einer etwas
    // groesseren Schrift ueberlaufen lassen statt umzubrechen. Der Vollmond
    // ist ohnehin eine eigene Aussage — also eine eigene Pille. Damit ist
    // keine laenger als rund 25 Zeichen, und der Block bleibt auf jedem
    // Geraet ruhig.
    // 2026-08-26 (Norm A + B9): Das Phasen-Emoji des Servers stand direkt
    // neben dem ausgeschriebenen Namen — es war das einzige bunte Zeichen der
    // Seite und sagte nichts, was "Vollmond" nicht schon sagt.
    mondTeil.textContent = `${kosmos.mond.name} · ${kosmos.mond.beleuchtung_pct} %`;
    kz.appendChild(mondTeil);
    // 2026-08-26 (Norm B6, "keine Zeile wiederholt, was darueber steht"):
    // Ist heute SELBST Vollmond, standen zwei Pillen nebeneinander, die beide
    // mit demselben Wort begannen — "Vollmond · 98 %" und "Vollmond 28.08.".
    // Die zweite sagt dann nur noch, wann der naechste ist.
    if (vollStr) {
      const heuteVollmond = /vollmond/i.test(kosmos.mond.name);
      const vollTeil = document.createElement("span");
      vollTeil.className = "vf-kp";
      vollTeil.textContent = heuteVollmond
        ? `nächster ${vollStr}`
        : `Vollmond ${vollStr}`;
      kz.appendChild(vollTeil);
    }
    if (kosmos.kp) {
      const kpTeil = document.createElement("span");
      kpTeil.className = `vf-kp vf-kp--${kosmos.kp.label === "Sturm" ? "sturm" : kosmos.kp.label}`;
      kpTeil.textContent = `Erdmagnetfeld ${kosmos.kp.label} (Kp ${kosmos.kp.kp})`;
      kz.appendChild(kpTeil);
    }
    // 2026-07-22 (Martin): Sonnenaktivitaet — GOES-Roentgenklasse mit
    // 6h-Spitze, gleiche Ampel-Sprache wie die Erdmagnetfeld-Pille.
    if (kosmos.sonne) {
      const so = document.createElement("span");
      const stufe =
        kosmos.sonne.label === "ruhig"
          ? "ruhig"
          : kosmos.sonne.label === "aktiv"
            ? "unruhig"
            : "sturm";
      so.className = `vf-kp vf-kp--${stufe}`;
      so.textContent = `Sonne ${kosmos.sonne.label} (${kosmos.sonne.klasse} · Spitze ${kosmos.sonne.spitze})`;
      kz.appendChild(so);
    }
    body.appendChild(kz);
  }

  if (!s || s.configured === false) {
    hinweis(body, "WHOOP ist auf dem Server nicht eingerichtet.");
    return;
  }
  if (s.connected === false) {
    /*
     * ⚠️ 16.09.2026 (Martin) — DAS WAR EINE WEGBESCHREIBUNG, KEIN HINWEIS.
     *
     * *„Die Information mit dem Whoop-Band kann man eigentlich ganz nebenbei
     * anstellen, weil die meisten kein Whoop haben. Albert hat zum Beispiel
     * keine, er haette eine Apple Watch, die man eventuell verknuepfen
     * koennte, aber das ist nicht so relevant. Also bitte nur als Hinweis,
     * falls jemand ein Whoop hat, dass man das anknuepfen koennte."*
     *
     * Vorher stand hier eine Aufforderung mit Menuepfad — fuer die grosse
     * Mehrheit, die kein Band besitzt, eine Anleitung zu einem Geraet, das
     * sie nicht hat. Jetzt ein Nebensatz: WENN du eines traegst, geht das.
     */
    hinweis(
      body,
      "Falls du ein WHOOP-Band trägst, lässt es sich in den Einstellungen anknüpfen — dann stehen hier auch Erholung, Schlaf und Belastung.",
    );
    return;
  }
  if (!s.ok || !s.tage || s.tage.length === 0) {
    hinweis(
      body,
      s?.error
        ? `Abruf fehlgeschlagen: ${s.error}`
        : "Noch keine Daten — nach der ersten Nacht mit dem Band erscheint hier deine Verfassung.",
    );
    return;
  }

  const heute = s.tage[0];
  const tage = s.tage;

  // ── Heute-Block ─────────────────────────────────────────────────────────
  const heuteWrap = document.createElement("section");
  heuteWrap.className = "vf-heute";

  const score = document.createElement("div");
  score.className = "vf-score vf-score--ring";
  if (heute.erholung_pct != null) score.dataset.ampel = ampel(heute.erholung_pct);
  // 2026-07-27 (Audit-Punkt 4): RING statt Prozentzahl im Kasten. Eine Zahl
  // sagt „43". Ein Ring zeigt, wie viel von einem Ganzen das ist — man sieht
  // es, bevor man es liest. Genau darin liegt der optische Vorsprung der
  // WHOOP-App.
  // Der Ring war laengst da (whoopRing in tool-cards.ts, bisher nur in den
  // Chat-Karten). Seine Farbklassen decken sich exakt mit ampel(), es braucht
  // also keine Uebersetzung.
  // Der Behaelter .vf-score BLEIBT — an ihm haengen die Breiten- und
  // Umbruch-Regeln, die heute frueh erst repariert wurden.
  score.appendChild(
    whoopRing(
      heute.erholung_pct != null ? heute.erholung_pct / 100 : null,
      ampel(heute.erholung_pct),
      heute.erholung_pct != null ? `${heute.erholung_pct} %` : "—",
      "Erholung",
    ),
  );
  // 2026-07-25 (Martin: "die Auswertung von Whoop schoener"): Eine
  // Prozentzahl ohne Deutung ist keine Auskunft. Ein Wort sagt, was sie
  // bedeutet — dieselbe Ampel-Schwelle, die auch die Farbe setzt.
  if (heute.kalibriert_noch) {
    const k = document.createElement("small");
    k.textContent = "Band kalibriert noch";
    score.appendChild(k);
  }
  if (heute.erholung_pct != null) heuteWrap.appendChild(score);

  const facts = document.createElement("div");
  facts.className = "vf-facts";
  // 2026-07-27 (Audit-Punkt 8): Jede Kachel bekommt Verlauf UND Bezug.
  // Vorher stand dort eine nackte Zahl — „27 ms" sagt einem Laien nichts.
  const NS = "http://www.w3.org/2000/svg";
  const median = (a: number[]): number => {
    const q = [...a].sort((p, r) => p - r);
    const m = Math.floor(q.length / 2);
    return q.length % 2 ? (q[m]) : ((q[m - 1]) + (q[m])) / 2;
  };
  /**
   * MEDIAN, nicht Mittel: eine Fiebernacht oder ein Marathon verschiebt das
   * Mittel spuerbar, den Median kaum. Fehlende Tage fallen raus; unter fuenf
   * Vergleichstagen waere „dein Schnitt" gelogen — dann lieber kein Satz.
   */
  const bezug = (
    reihe: Array<number | null | undefined>,
    nk: 0 | 1,
    einheit = "",
  ): string | null => {
    const h = reihe[0];
    if (h == null || !isFinite(h)) return null;
    const rest = reihe
      .slice(1)
      .filter((v): v is number => v != null && isFinite(v));
    if (rest.length < 5) return null;
    const diff = h - median(rest);
    if (Math.abs(diff) < (nk === 0 ? 0.5 : 0.05)) return "wie dein Schnitt";
    const b = Math.abs(diff).toFixed(nk).replace(".", ",");
    return `${b}${einheit ? " " + einheit : ""} ${diff > 0 ? "über" : "unter"} deinem Schnitt`;
  };
  /**
   * Sparkline ohne Bibliothek. Die Linie wird per preserveAspectRatio="none"
   * auf die Kachelbreite gezogen — deshalb ist vector-effect
   * non-scaling-stroke PFLICHT, sonst ist sie waagrecht duenn und senkrecht
   * dick. Aus demselben Grund ist der Heute-Punkt kein <circle> (das wuerde
   * zur Ellipse), sondern ein Null-Segment mit runder Kappe.
   * Luecken brechen den Pfad (M statt L) statt ihn zu ueberbruecken.
   */
  const spark = (reihe: Array<number | null | undefined>): SVGSVGElement | null => {
    const n = reihe.length;
    const gueltig = reihe.filter((v): v is number => v != null && isFinite(v));
    if (n < 3 || gueltig.length < 3) return null;
    const min = Math.min(...gueltig);
    const spanne = Math.max(...gueltig) - min || 1;
    const alt = [...reihe].reverse(); // links alt, rechts heute
    const x = (i: number): string => ((i / (n - 1)) * 100).toFixed(2);
    const y = (v: number): string => (25 - ((v - min) / spanne) * 22).toFixed(2);
    let d = "";
    let luecke = true;
    alt.forEach((v, i) => {
      if (v == null || !isFinite(v)) {
        luecke = true;
        return;
      }
      d += `${luecke ? "M" : "L"}${x(i)} ${y(v)}`;
      luecke = false;
    });
    const svg = document.createElementNS(NS, "svg");
    svg.setAttribute("viewBox", "0 0 100 28");
    svg.setAttribute("preserveAspectRatio", "none");
    svg.setAttribute("aria-hidden", "true");
    svg.setAttribute("focusable", "false");
    svg.setAttribute("class", "vf-spark");
    const p = document.createElementNS(NS, "path");
    p.setAttribute("class", "vf-spark-linie");
    p.setAttribute("d", d);
    p.setAttribute("stroke-width", "1.5");
    p.setAttribute("stroke-linecap", "round");
    p.setAttribute("stroke-linejoin", "round");
    p.setAttribute("vector-effect", "non-scaling-stroke");
    svg.appendChild(p);
    const li = alt.reduce(
      (acc: number, v, i) => (v != null && isFinite(v) ? i : acc),
      -1,
    );
    if (li >= 0) {
      const wert = alt[li] as number;
      const dot = document.createElementNS(NS, "path");
      dot.setAttribute("class", "vf-spark-punkt");
      dot.setAttribute("d", `M${x(li)} ${y(wert)}L${x(li)} ${y(wert)}`);
      dot.setAttribute("stroke-width", "3.5");
      dot.setAttribute("stroke-linecap", "round");
      dot.setAttribute("vector-effect", "non-scaling-stroke");
      svg.appendChild(dot);
    }
    return svg;
  };
  const fakt = (
    label: string,
    wert: string,
    reihe?: Array<number | null | undefined>,
    opts?: { nk?: 0 | 1; einheit?: string; laufend?: boolean },
  ): void => {
    const f = document.createElement("div");
    f.className = "vf-fakt";
    /*
     * ⚠️ 2026-08-30 — Reihenfolge geaendert: erst der NAME, dann die Zahl.
     *
     * Vorher stand die Zahl zuerst, der Bezugssatz in der Mitte und der
     * Name ganz rechts — die Zeile war `justify-content: space-between`,
     * das schob drei verschiedene Dinge an die Raender. Im Browser
     * gemessen: Wert bei x=16, Bezug bei 159, Name bei 343.
     *
     * Man las also „36 ms … wie dein Schnitt … HRV" — die Zahl kam an,
     * bevor man wusste, wovon sie handelt, und der Name als Letztes. Genau
     * dasselbe Muster wie auf der Offen-Seite: `space-between` ist keine
     * Anordnung, sondern der Verzicht darauf.
     *
     * Jetzt: Name links (leise), Zahl rechts (gross) — zwei Spalten, die
     * sich untereinander vergleichen lassen. Der Bezugssatz haengt darunter
     * am Namen, wohin er gehoert, und der Verlauf laeuft ueber beide.
     */
    const l = document.createElement("span");
    l.textContent = label;
    f.append(l);
    const v = document.createElement("strong");
    v.textContent = wert;
    f.append(v);
    // Bewusst <small> und nicht <span>: die bestehende Regel `.vf-fakt span`
    // setzt Grossbuchstaben und Sperrung — der Bezugssatz ist Fliesstext.
    const satz =
      reihe && !opts?.laufend ? bezug(reihe, opts?.nk ?? 0, opts?.einheit) : null;
    if (satz) {
      const b = document.createElement("small");
      b.className = "vf-bezug";
      b.textContent = satz;
      f.append(b);
    }
    const sp = reihe ? spark(opts?.laufend ? reihe.slice(1) : reihe) : null;
    if (sp) f.append(sp);
    facts.appendChild(f);
  };
  if (heute.hrv_ms != null)
    fakt("HRV", `${heute.hrv_ms} ms`,
      tage.map((t) => t.hrv_ms), { nk: 0 });
  if (heute.ruhepuls != null)
    fakt("Ruhepuls", `${heute.ruhepuls}`,
      tage.map((t) => t.ruhepuls), { nk: 0 });
  if (heute.tagesbelastung != null)
    fakt(
      "Belastung",
      zahlDe(heute.tagesbelastung),
      tage.map((t) => t.tagesbelastung), { nk: 1, laufend: true },
    );
  if (heute.schlaf?.dauer_h != null)
    fakt(
      "Schlaf",
      `${heute.schlaf.dauer_h.toFixed(1).replace(".", ",")} h` +
        (heute.schlaf?.defizit_h != null && heute.schlaf.defizit_h < -0.5
          ? ` (${heute.schlaf.defizit_h.toFixed(1).replace(".", ",")})`
          : ""),
      tage.map((t) => t.schlaf?.dauer_h), { nk: 1, einheit: "h" },
    );
  if (heute.spo2_pct != null)
    fakt("SpO2", `${heute.spo2_pct.toFixed(1).replace(".", ",")} %`,
      tage.map((t) => t.spo2_pct), { nk: 1, einheit: "%" });
  if (heute.hauttemp_c != null)
    fakt(
      "Hauttemp.",
      `${heute.hauttemp_c.toFixed(1).replace(".", ",")} °C`,
      tage.map((t) => t.hauttemp_c), { nk: 1, einheit: "°C" },
    );
  if (heute.schlaf?.atemfrequenz != null)
    fakt(
      "Atmung",
      `${heute.schlaf.atemfrequenz.toFixed(1).replace(".", ",")}/min`,
      tage.map((t) => t.schlaf?.atemfrequenz), { nk: 1 },
    );
  if (heute.kalorien_kcal != null)
    fakt("Kalorien", `${heute.kalorien_kcal} kcal`,
      tage.map((t) => t.kalorien_kcal), { nk: 0, laufend: true });
  if (facts.childElementCount > 0) heuteWrap.appendChild(facts);
  if (heuteWrap.childElementCount > 0) body.appendChild(heuteWrap);

  // Schlafphasen-Balken (nur wenn vorhanden).
  const ph = heute.schlaf?.phasen;
  const phasenWerte: Array<[string, number | null]> = [
    ["Tief", ph?.tief_min ?? null],
    ["REM", ph?.rem_min ?? null],
    ["Leicht", ph?.leicht_min ?? null],
    ["Wach", ph?.wach_min ?? null],
  ];
  const summe = phasenWerte.reduce((a, [, v]) => a + (v ?? 0), 0);
  if (summe > 0) {
    const pWrap = document.createElement("div");
    pWrap.className = "vf-phasen";
    // 2026-07-25 (Martin, "Whoop schoener"): Der Balken stand ohne
    // Ueberschrift da — man musste raten, was er zeigt.
    const pTitel = document.createElement("div");
    pTitel.className = "vf-phasen-titel";
    pTitel.textContent = "Schlaf-Phasen";
    pWrap.appendChild(pTitel);
    const balken = document.createElement("div");
    balken.className = "vf-phasen-balken";
    const legende = document.createElement("div");
    legende.className = "vf-phasen-legende";
    for (const [name, min] of phasenWerte) {
      if (!min) continue;
      const seg = document.createElement("span");
      seg.className = `vf-phase vf-phase--${name.toLowerCase()}`;
      // Wie oben: nur der Messwert ist inline, die Regel steht im Blatt.
      seg.style.setProperty("--anteil", String(min));
      balken.appendChild(seg);
      const le = document.createElement("span");
      le.className = `vf-legende vf-phase--${name.toLowerCase()}`;
      le.textContent = `${name} ${Math.round((min / 60) * 10) / 10} h`;
      legende.appendChild(le);
    }
    const eff = heute.schlaf?.effizienz_pct;
    if (eff != null) {
      const le = document.createElement("span");
      le.className = "vf-legende";
      le.textContent = `Effizienz ${eff} %`;
      legende.appendChild(le);
    }
    pWrap.append(balken, legende);
    body.appendChild(pWrap);
  }

  // ── Achtsamkeits-Spiegel (Ticket #501): negative Selbst-Sprache ────────
  try {
    const ar = await fetch("/api/achtsamkeit/heute", {
      credentials: "include",
    });
    const aj = (await ar.json()) as {
      ok?: boolean;
      heute?: Array<{
        id: number;
        /* 2026-07-25: Zeitpunkt — der Server liefert ihn laengst mit, im
           Client-Typ fehlte er, deshalb war er nie nutzbar. */
        ts: string;
        /* 2026-07-26: Wie oft derselbe Satz im selben Moment fiel. */
        wiederholungen?: number;
        satz: string;
        kategorie: string;
        umformulierung: string | null;
        feedback: string | null;
      }>;
      wochen?: Array<{ kategorie: string; anzahl: number }>;
    };
    if (aj?.ok) {
      const KAT: Record<string, string> = {
        selbstabwertung: "Selbstabwertung",
        verallgemeinerung: "Verallgemeinerung",
        katastrophisieren: "Katastrophisieren",
        ohnmacht: "Ohnmachtssprache",
        mangel: "Mangel-Satz",
        etikett: "Selbst-Etikett",
        frust: "Frust-Ausbruch",
      };
      const heuteHits = (aj.heute ?? []).filter(
        (x) => x.feedback !== "stimmt-nicht",
      );
      const wochenVorab = aj.wochen ?? [];
      if (heuteHits.length === 0 && wochenVorab.length === 0) {
        /* keine echte Spur */
      } else {
      const sek = document.createElement("section");
      sek.className = "vf-achtsamkeit";
      const h = document.createElement("h3");
      h.className = "vf-sub vf-acht-kopf";
      const hTitel = document.createElement("span");
      hTitel.textContent = "Achtsamkeit — deine Sprache heute";
      h.appendChild(hTitel);
      if (heuteHits.length > 0) {
        // Zaehler als Blickfang — Martin sieht sonst nicht, dass hier
        // etwas wartet.
        const zahl = document.createElement("span");
        zahl.className = "vf-acht-zahl";
        zahl.textContent = String(heuteHits.length);
        h.appendChild(zahl);
      }
      sek.appendChild(h);
      // Raster: nutzt die breite Spalte (Desktop 2-3 Spalten).
      const grid = document.createElement("div");
      grid.className = "vf-acht-grid";
      sek.appendChild(grid);
      for (const hit of heuteHits.slice(0, 6)) {
        const karte = document.createElement("div");
        karte.className = "vf-acht-karte";
        // 2026-07-25 (Martin: "uebersichtlicher, vielleicht auch mit Datum,
        // wann ich was gesagt habe"): Kopfzeile mit Uhrzeit und Kategorie.
        // Die Uhrzeit kam schon immer mit (`ts`), wurde aber nie gezeigt —
        // ohne sie laesst sich ein Satz nicht einordnen.
        const kopf = document.createElement("div");
        kopf.className = "vf-acht-kopfzeile";
        const zeit = document.createElement("span");
        zeit.className = "vf-acht-zeit";
        const d = new Date(hit.ts);
        zeit.textContent = isNaN(d.getTime())
          ? ""
          : d.toLocaleTimeString("de-AT", {
              hour: "2-digit",
              minute: "2-digit",
            });
        const kat = document.createElement("span");
        kat.className = "vf-acht-kat";
        kat.textContent = KAT[hit.kategorie] ?? hit.kategorie;
        kopf.append(zeit, kat);
        // 2026-07-26 (Martin): Wiederholungen im selben Moment werden
        // zusammengefasst — aber NICHT verschwiegen. Wer denselben Satz
        // dreimal sagt, soll das sehen.
        if ((hit.wiederholungen ?? 1) > 1) {
          const mehr = document.createElement("span");
          mehr.className = "vf-acht-mehr";
          mehr.textContent = `${hit.wiederholungen}\u00d7`;
          antippbar(
            mehr,
            "So oft ist dieser Satz in diesem Moment gefallen",
            kopf,
          );
          kopf.appendChild(mehr);
        }
        const satz = document.createElement("p");
        satz.className = "vf-acht-satz";
        satz.textContent = `„${hit.satz}“`;
        karte.append(kopf, satz);
        // Der Vorschlag ist eine ANDERE Stimme als der zitierte Satz —
        // deshalb ein eigener Block darunter statt per Gedankenstrich an
        // die Kategorie geklebt.
        if (hit.umformulierung) {
          const vor = document.createElement("p");
          vor.className = "vf-acht-vorschlag";
          const lbl = document.createElement("span");
          lbl.className = "vf-acht-vorschlag-lbl";
          lbl.textContent = "vielleicht";
          vor.appendChild(lbl);
          vor.appendChild(
            document.createTextNode(`„${hit.umformulierung}“`),
          );
          karte.appendChild(vor);
        }
        if (!hit.feedback) {
          const reihe = document.createElement("div");
          reihe.className = "vf-acht-reihe";
          const mk = (label: string, stimmt: boolean): HTMLButtonElement => {
            const b = document.createElement("button");
            b.type = "button";
            b.className = "vf-acht-btn";
            b.textContent = label;
            b.addEventListener("click", () => {
              b.disabled = true;
              void fetch("/api/achtsamkeit/feedback", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                credentials: "include",
                body: JSON.stringify({ id: hit.id, stimmt }),
              })
                .then((r) => {
                  // fetch rejectet bei 4xx/5xx NICHT — ohne ok-Pruefung
                  // verschwand die Karte auch bei fehlgeschlagenem
                  // Speichern (Wurzel des Reload-Verlusts, pre.1909).
                  if (!r.ok) throw new Error(String(r.status));
                  reihe.remove();
                  if (!stimmt) karte.remove();
                })
                .catch(() => {
                  b.disabled = false;
                });
            });
            return b;
          };
          reihe.append(mk("Stimmt", true), mk("Stimmt nicht", false));
          karte.appendChild(reihe);
        }
        grid.appendChild(karte);
      }
      const wochen = aj.wochen ?? [];
      if (wochen.length > 0) {
        const wz = document.createElement("p");
        wz.className = "vf-acht-woche";
        wz.textContent =
          "Letzte 7 Tage: " +
          wochen
            .map((w) => `${KAT[w.kategorie] ?? w.kategorie} ${w.anzahl}×`)
            .join(" · ");
        sek.appendChild(wz);
      }
      body.appendChild(sek);
      }
    }
  } catch {
    /* Spiegel ist Kür */
  }

  // ── Frage-Knopf: Werte ins Gespraech tragen ────────────────────────────

  // ── 14-Tage-Verlauf ────────────────────────────────────────────────────
  const trendTage = [...s.tage].reverse().filter((t) => t.erholung_pct != null);
  if (trendTage.length > 0) {
  const trendTitel = document.createElement("h3");
  trendTitel.className = "vf-titel";
  trendTitel.textContent = "Verlauf (14 Tage)";
  body.appendChild(trendTitel);

  const trend = document.createElement("div");
  trend.className = "vf-trend";
  for (const t of trendTage) {
    const col = document.createElement("div");
    col.className = "vf-trend-tag";
    const bar = document.createElement("span");
    bar.className = "vf-trend-balken";
    bar.dataset.ampel = ampel(t.erholung_pct);
    /*
     * 2026-08-30 (Martins Regel: Gestaltung ins CSS, nichts inline):
     * Die HOEHE ist eine Messgroesse, kein Gestaltungsentscheid — sie kann
     * nicht im Blatt stehen. Was ins Blatt gehoert, ist WIE daraus eine
     * Hoehe wird; hier fliesst nur noch die Zahl ein.
     *
     * ⚠️ Die Untergrenze von 6 % bleibt: ein Tag mit 0 % Erholung waere
     * sonst unsichtbar und saehe aus wie ein fehlender Tag.
     */
    bar.style.setProperty("--anteil", String(Math.max(6, t.erholung_pct ?? 0)));
    const schlafInfo =
      t.schlaf?.dauer_h != null
        ? `, Schlaf ${t.schlaf.dauer_h.toFixed(1).replace(".", ",")} h`
        : "";
    const wtag = t.datum
      ? new Date(t.datum).toLocaleDateString("de-AT", { weekday: "short" })
      : "";
    const lbl = document.createElement("small");
    lbl.textContent = fmtDatum(t.datum);
    col.append(bar, lbl);
    // Anker ist die ganze Reihe, nicht die Saeule: .vf-trend ist ein
    // Flex-Streifen — die Zeile darf nicht zwischen die Balken rutschen.
    antippbar(
      col,
      `${wtag} ${fmtDatum(t.datum)}: ${t.erholung_pct ?? "—"} %${schlafInfo}`,
      trend,
    );
    trend.appendChild(col);
  }
  body.appendChild(trend);
  }

  // ── Trainings ──────────────────────────────────────────────────────────
  if (s.workouts && s.workouts.length > 0) {
    const wTitel = document.createElement("h3");
    wTitel.className = "vf-titel";
    wTitel.textContent = "Letzte Trainings";
    body.appendChild(wTitel);
    // 2026-07-22 (Martin): schlanke TABELLE statt Karten-Zeilen — mehr
    // Information auf weniger Flaeche, gerade am Smartphone.
    const tab = document.createElement("div");
    tab.className = "vf-workout-tabelle";
    const kopfZelle = (t: string, zahl = false): void => {
      const el = document.createElement("span");
      el.className = "vf-wt-kopf" + (zahl ? " vf-wt-zahl" : "");
      el.textContent = t;
      tab.appendChild(el);
    };
    kopfZelle("");
    kopfZelle("Sport");
    kopfZelle("min", true);
    kopfZelle("Bel.", true);
    kopfZelle("Puls", true);
    kopfZelle("kcal", true);
    const zelle = (t: string, klasse: string): void => {
      const el = document.createElement("span");
      el.className = "vf-wt-zelle " + klasse;
      el.textContent = t;
      tab.appendChild(el);
    };
    for (const w of s.workouts.slice(0, 7)) {
      zelle(fmtDatum(w.start), "vf-wt-datum");
      zelle(sportName(w.sport), "vf-wt-sport");
      zelle(w.dauer_min != null ? String(Math.round(w.dauer_min)) : "—", "vf-wt-zahl");
      zelle(w.belastung != null ? zahlDe(w.belastung) : "—", "vf-wt-zahl");
      zelle(w.puls != null ? String(w.puls) : "—", "vf-wt-zahl");
      zelle(w.kcal != null ? String(w.kcal) : "—", "vf-wt-zahl");
    }
    body.appendChild(tab);
  }

  // ── Muster (Korrelationen) — ehrlich erst ab genug Datenlage ──────────
  const moodsBewertet = (muster?.moods ?? []).filter(
    (m2) => m2.seele != null,
  );
  if (moodsBewertet.length >= 10) {
    const mTitel = document.createElement("h3");
    mTitel.className = "vf-titel";
    mTitel.textContent = "Muster";
    body.appendChild(mTitel);
    const erhVon = new Map(
      (s?.tage ?? []).map((t) => [t.datum ?? "", t.erholung_pct]),
    );
    const kpVon = new Map(
      (muster?.kosmos ?? []).map((k2) => [k2.day, k2.kp]),
    );
    const schnitt = (werte: number[]): number | null =>
      werte.length
        ? Math.round(werte.reduce((a, b) => a + b, 0) / werte.length)
        : null;
    const paare: Array<[string, number[], number[]]> = [
      ["Erholung < 50 % vs. darüber", [], []],
      ["Erdmagnetfeld unruhig (Kp ≥ 3) vs. ruhig", [], []],
    ];
    for (const m2 of moodsBewertet) {
      if (m2.seele == null) continue;
      const erh = erhVon.get(m2.day);
      if (typeof erh === "number" && paare[0])
        (erh < 50 ? paare[0][1] : paare[0][2]).push(m2.seele);
      const kp = kpVon.get(m2.day);
      if (typeof kp === "number" && paare[1])
        (kp >= 3 ? paare[1][1] : paare[1][2]).push(m2.seele);
    }
    const liste = document.createElement("div");
    liste.className = "vf-muster";
    for (const [label, a, b] of paare) {
      const sa = schnitt(a);
      const sb = schnitt(b);
      if (sa == null || sb == null || a.length < 3 || b.length < 3) continue;
      const row = document.createElement("p");
      row.className = "vf-muster-zeile";
      row.textContent =
        `${label}: Seelen-Grundton ${sa} vs. ${sb} ` +
        `(${a.length}/${b.length} Tage)`;
      liste.appendChild(row);
    }
    if (liste.hasChildNodes()) {
      body.appendChild(liste);
    } else {
      mTitel.remove();
    }
  }

  const quelle = document.createElement("p");
  quelle.className = "vf-quelle";
  quelle.textContent =
    "Quelle: WHOOP · trennen unter Einstellungen → Verbindungen";
  body.appendChild(quelle);
}

function hinweis(body: HTMLElement, text: string): void {
  const p = document.createElement("p");
  p.className = "vf-hinweis";
  p.textContent = text;
  body.appendChild(p);
}
